import React, { useCallback, useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { useDebouncedCallback } from "use-lodash-debounce";
import {
  Wrapper,
  InputField,
  Textarea,
  Label,
  ConfirmationValueInputWrapper,
} from "./styled";
import ValidationBar from "../common3/ValidationBar";
import get from "lodash/get";
import setDefaultValue from "./setDefaultValue";
import HelpIcon from "../common3/HelpBox/Icon";
import HelpBox from "../common3/HelpBox";
import parseValidationError from "./helpers/parseValidationError";

const Input = (props) => {
  const {
    intl,
    extra = {},
    value, // <-- important not to set this to anything. We use undefined to check against default value.
    confirmValue,
    save = () => {},
    disableValidation = false,
    validationError = "",
    id,
    user = {},
    label: propsLabel,
    onChange = undefined,
    height: propsHeight = undefined,
    onKeyDown = () => {},
    htmlTemplates = {},
    dispatchEvent = () => {},
  } = props;
  let {
    name,
    label = "",
    height = "55",
    multipleLines = false,
    multipleLinesAmount = 5,
    type = "text",
    subLabel = "",
    helpBoxAlwaysVisible = false,
    helpBoxOnFocus = false,
    helpBoxIcon = false,
    helpBoxTemplateId = null,
    helpBoxTemplate = null,
    autoCompleteValue = "",
    confirmationInputLabel = "Input.confirmationInput.defaultLabel",
  } = extra;
  const { validation = {}, events = {}, enableConfirmation = false } = extra;
  const { required = false } = validation;
  const { onChange: onChangeEvent } = events;
  const disabled = props.disabled || extra.disabled;
  const { error, errorMessage } = parseValidationError(validationError);

  const [inputValue, setInputValue] = useState(value || "");
  const [inputFocused, setInputFocused] = useState(false);
  const [confirmationValue, setConfirmationValue] = useState(
    confirmValue || ""
  );

  const [_errorMessage, setErrorMessage] = useState(errorMessage || null);

  const [helpBoxOpen, setHelpBoxOpen] = useState(
    helpBoxAlwaysVisible ? true : false
  );

  if (!autoCompleteValue) {
    autoCompleteValue = extra.defaultValue;
  }

  useEffect(() => {
    const { errorMessage } = parseValidationError(validationError);
    setErrorMessage(errorMessage);
  }, [
    validationError.critical,
    validationError.error,
    validationError.warning,
  ]);

  /**
   * Effect to handle incoming new values, i.e. changes from outside the component
   *
   * Notice that we do not call debouceSetValue, but we cancel all values
   */
  useEffect(() => {
    if (value === undefined || value === null) {
      const defaultValue = setDefaultValue({ extra, user, props, context: {} });

      if (defaultValue) {
        setInputValue(defaultValue);
        save({ key: id, value: defaultValue });
      }
    } else if (inputValue !== value && !inputFocused) {
      /**
       * Why do we do replace? In summary: This is a very technical issue, and do not remove it.
       * It is because JSON.parse does not accept \n, and when we run filterString with newlines it will
       * crash. So we have to do a couple of things to fix this. Among these are replacing string \n with real newline-chars.
       */
      setInputValue(
        value && typeof value === "string" && value.replaceAll("\\n", "\n")
      );
      debounceSave.cancel();
    }
  }, [value]);

  /**
   * Callback to initiate debouce saving, this is called when a user modifies a value
   */
  const debouncedSave = useCallback(
    ({ key, value }) => {
      save({ key, value });
      if (value && onChangeEvent) {
        dispatchEvent("onChange", value);
      }
    },
    [value]
  );

  const debounceSave = useDebouncedCallback(
    debouncedSave,
    props.debounceTimer ? props.debounceTimer : 100
  );

  const onBlur = useCallback(() => {
    setInputFocused(false);
    if (inputValue !== value) {
      save({ key: id, value: inputValue });
    }
  }, [inputValue, save, id, value]);

  const hasConfirmationValidationError = (value, confirmationValue) => {
    if (value !== confirmationValue) {
      setErrorMessage("confirmation-not-matching");
      return true;
    } else if (validationError) {
      setErrorMessage(parseValidationError(validationError).errorMessage);
      return true;
    } else {
      setErrorMessage(null);
      return false;
    }
  };
  useEffect(() => {
    hasConfirmationValidationError(inputValue, confirmationValue);
  }, []);

  const getOnChangeValue = (confirm = false) => {
    return (event) => {
      const { value } = event.target;
      debounceSave({
        value,
        key: confirm ? `inputValidation.${id}` : id,
      });
      if (enableConfirmation) {
        if (confirm) {
          hasConfirmationValidationError(inputValue, value);
        } else {
          hasConfirmationValidationError(value, confirmationValue);
        }
      }

      if (confirm) {
        setConfirmationValue(value);
      } else {
        setInputValue(value);
      }
    };
  };

  const onValueChangeConfirm = getOnChangeValue(true);
  const onValueChange = getOnChangeValue();

  const fixedLabel = [
    "time",
    "week",
    "date",
    "month",
    "datetime-local",
  ].includes(type);

  const _helpBoxTemplate =
    helpBoxTemplateId === "_custom"
      ? helpBoxTemplate
      : get(htmlTemplates, [helpBoxTemplateId, "html"], "");

  height = propsHeight ? propsHeight : multipleLinesAmount > 1 ? 55 : height;

  return (
    <Wrapper className={required ? "sp-required" : ""}>
      {!helpBoxAlwaysVisible && helpBoxIcon && (
        <HelpIcon toggle={() => setHelpBoxOpen(!helpBoxOpen)} />
      )}
      <Label
        htmlFor={id}
        focused={inputFocused}
        inputLength={inputValue}
        fixed={fixedLabel}
        error={error}
        height={height}
        disabled={disabled}
      >
        {propsLabel ? propsLabel : label}
      </Label>
      {multipleLines === true ? (
        <Textarea
          id={id}
          name={name}
          rows={multipleLinesAmount}
          value={inputValue}
          onChange={onValueChange}
          onFocus={() => {
            setInputFocused(true);
            !helpBoxAlwaysVisible && helpBoxOnFocus && setHelpBoxOpen(true);
          }}
          onBlur={() => {
            setInputFocused(false);
            !helpBoxAlwaysVisible && helpBoxOnFocus && setHelpBoxOpen(false);
          }}
          onKeyDown={onKeyDown}
          disabled={disabled}
          error={error}
          autoComplete={autoCompleteValue}
          aria-label={label || "TextArea"}
        />
      ) : (
        <>
          <InputField
            id={id}
            name={name}
            type={type}
            value={inputValue}
            onChange={onChange ? onChange : onValueChange}
            onFocus={() => {
              setInputFocused(true);
              !helpBoxAlwaysVisible && helpBoxOnFocus && setHelpBoxOpen(true);
            }}
            onBlur={() => {
              onBlur();
              !helpBoxAlwaysVisible && helpBoxOnFocus && setHelpBoxOpen(false);
            }}
            max={
              type === "date" // This check limits the year to 4 digits when typing the date instead of the default 6 digits.
                ? "9999-12-31"
                : type === "datetime-local"
                ? "9999-12-31T23:59"
                : undefined
            }
            disabled={disabled}
            onKeyDown={onKeyDown}
            height={height}
            error={error}
            autoComplete={autoCompleteValue}
            aria-label={label || "Input"}
          />
          {enableConfirmation && (
            <ConfirmationValueInputWrapper>
              <Label
                htmlFor={`${id}-confirm`}
                focused={inputFocused}
                inputLength={inputValue}
                fixed={fixedLabel}
                error={error}
                height={height}
                disabled={disabled}
              >
                {confirmationInputLabel
                  ? intl.formatMessage({ id: confirmationInputLabel })
                  : ""}
              </Label>
              <InputField
                id={`${id}-confirm`}
                value={confirmationValue}
                height={height}
                disabled={disabled}
                type={type}
                onChange={onValueChangeConfirm}
              />
            </ConfirmationValueInputWrapper>
          )}
        </>
      )}
      {(helpBoxAlwaysVisible || helpBoxOnFocus || helpBoxIcon) &&
        _helpBoxTemplate &&
        helpBoxOpen && (
          <HelpBox template={_helpBoxTemplate} props={props} value={value} />
        )}
      <ValidationBar
        error={enableConfirmation ? _errorMessage : errorMessage}
        subLabel={subLabel}
        disableValidation={disableValidation}
      />
    </Wrapper>
  );
};

export default injectIntl(Input);
